import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Flex, Spin, Typography, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import {
  UrtVersionSummary,
  getAdminReferralTermsHistory,
  downloadAdminReferralTermsPdf,
} from '@/api/referralTerms';
import { downloadBase64Pdf } from '@/helpers/downloadBase64Pdf';

interface Props {
  franchiseId: string;
}

// TCX-739: admin version-history for a franchisor's Universal Referral Terms.
// Renders the BODY of the accordion panel wired up in FranchisorDetailed.tsx.
// Mirrors the LegalAcceptanceHistoryModal patterns (StatusPill badge, per-row
// download with loading state, base64 -> download) but as an inline audit list.

const ACCENT = '#1C8271';

// How many rows the "Show all …" toggle can ever reveal. A rolling history of
// up to 10 versions is kept per franchisor; older versions remain in the DB but
// are intentionally not surfaced here (server retains them).
const MAX_VISIBLE_VERSIONS = 10;
const DEFAULT_VISIBLE_VERSIONS = 3;

// "Signed Jun 10, 2026" — matches the design spec date format (date-fns is the
// app's date lib; same 'MMM d, yyyy' pattern used in helpers/subscription.ts).
const formatSignedDate = (value?: string | null): string => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return format(date, 'MMM d, yyyy');
};

// Green "CURRENT" pill on the single published version (StatusPill pattern from
// LegalAcceptanceHistoryModal — dot + label, accent #1C8271).
const CurrentBadge = (): JSX.Element => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      backgroundColor: `${ACCENT}14`,
      color: ACCENT,
      border: `1px solid ${ACCENT}`,
      borderRadius: 12,
      padding: '0 10px',
      height: 22,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.5,
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: ACCENT,
      }}
    />
    CURRENT
  </span>
);

export default function UniversalReferralTermsHistory({
  franchiseId,
}: Props): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const [downloadingVersion, setDownloadingVersion] = useState<number | null>(
    null,
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-referral-terms-history', franchiseId],
    queryFn: () => getAdminReferralTermsHistory(franchiseId),
    enabled: !!franchiseId,
  });

  const handleDownload = async (version: number) => {
    setDownloadingVersion(version);
    try {
      const { file, filename } = await downloadAdminReferralTermsPdf(
        franchiseId,
        version,
      );
      if (!file || typeof file !== 'string') {
        message.error('Download failed: server returned an invalid file.');
        return;
      }
      downloadBase64Pdf(file, filename);
    } catch (error) {
      // Prefer the NestJS error message over axios's generic status text.
      const err = error as
        | { response?: { data?: { message?: string } }; message?: string }
        | undefined;
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Download failed. Please try again.',
      );
    } finally {
      setDownloadingVersion(null);
    }
  };

  if (isLoading) {
    return (
      <div className={'details-section'}>
        <Flex justify={"center"} style={{ padding: '24px 0' }}>
          <Spin />
        </Flex>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={'details-section'}>
        <Alert
          type={"error"}
          showIcon
          message={"Couldn't load the referral terms history"}
          description={"Something went wrong fetching this franchisor's Universal Referral Terms. Please close this section and try again."}
        />
      </div>
    );
  }

  const totalVersions = data?.totalVersions ?? 0;

  if (totalVersions === 0) {
    return (
      <div className={'details-section'}>
        <Typography.Text className={'general-info-text'}>
          No Universal Referral Terms have been published for this franchisor
          yet.
        </Typography.Text>
      </div>
    );
  }

  // Versions come back descending from the API; render newest first regardless.
  const versions: UrtVersionSummary[] = [...(data?.versions ?? [])].sort(
    (a, b) => b.version - a.version,
  );

  // Show the 3 most recent by default; "Show all" expands up to 10 rows — never
  // more, even if additional versions exist server-side.
  const cap = showAll ? MAX_VISIBLE_VERSIONS : DEFAULT_VISIBLE_VERSIONS;
  const visible = versions.slice(0, cap);

  // The toggle only matters when there are more rows than the default view
  // reveals (capped by what we're willing to show at all).
  const showableCount = Math.min(versions.length, MAX_VISIBLE_VERSIONS);
  const hasMoreToShow = showableCount > DEFAULT_VISIBLE_VERSIONS;

  return (
    <div className={'details-section'}>
      <Typography.Text
        className={'general-info-text'}
        style={{ display: 'block', marginBottom: 16, fontWeight: 500 }}
      >
        {totalVersions} on file
      </Typography.Text>

      <Flex vertical gap={12}>
        {visible.map((v) => (
          // Stacked layout: the General Info accordion column is narrow (~260px),
          // so version+badge+download sit on the top line and the signer / signed
          // date go beneath — keeps everything readable without char-wrapping.
          <Flex
            key={v.version}
            vertical
            gap={2}
            style={{
              borderBottom: '1px solid #f0f0f2',
              paddingBottom: 12,
            }}
          >
            <Flex align={"center"} justify={"space-between"} gap={8}>
              <Flex
                align={"center"}
                gap={8}
                style={{ minWidth: 0, flexWrap: 'wrap' }}
              >
                <Typography.Text
                  className={'general-info-value'}
                  style={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                >
                  v{v.version}.0
                </Typography.Text>
                {v.isCurrent && <CurrentBadge />}
              </Flex>
              <Button
                type={"text"}
                icon={<DownloadOutlined />}
                style={{ color: ACCENT, flex: 'none' }}
                loading={downloadingVersion === v.version}
                onClick={() => handleDownload(v.version)}
                aria-label={`Download version ${v.version}.0 PDF`}
              />
            </Flex>
            <Typography.Text
              className={'general-info-value'}
              ellipsis={{ tooltip: v.signerName || 'N/A' }}
            >
              {v.signerName || 'N/A'}
            </Typography.Text>
            <Typography.Text className={'general-info-text'}>
              Signed {formatSignedDate(v.signedAt)}
            </Typography.Text>
          </Flex>
        ))}
      </Flex>

      {hasMoreToShow && (
        <Button
          type={"link"}
          onClick={() => setShowAll((prev) => !prev)}
          style={{ padding: 0, height: 'auto', color: ACCENT, marginTop: 4 }}
        >
          {showAll ? 'Show fewer versions' : `Show all ${totalVersions} versions`}
        </Button>
      )}

      <Typography.Text
        type={"secondary"}
        style={{ display: 'block', marginTop: 16, fontSize: 12 }}
      >
        A rolling history of up to 10 versions is kept per franchisor. Older
        versions remain available in the database.
      </Typography.Text>
    </div>
  );
}
